'use client'

import * as React from 'react'

import { HiMiniCheck } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/common/Command'
import Popover, {
  type PopoverContentProps,
  type PopoverProps,
} from '@/components/common/PopoverV2'

const Combobox = Popover
const ComboboxTrigger = Popover.Trigger

export interface ComboboxProps extends PopoverProps {}
export interface ComboboxContentProps extends PopoverContentProps {
  shouldFilter?: boolean
}

const ComboboxContent = React.forwardRef<HTMLDivElement, ComboboxContentProps>(
  ({ children, shouldFilter = true, ...props }, forwardedRef) => (
    <Popover.Content
      ref={forwardedRef}
      {...props}
      portal={props.portal ?? false}
    >
      <Command shouldFilter={shouldFilter}>{children}</Command>
    </Popover.Content>
  ),
)

const ComboboxInput = CommandInput
const ComboboxList = CommandList
const ComboboxEmpty = CommandEmpty
const ComboboxGroup = CommandGroup
const ComboboxItem = CommandItem
const ComboboxSeparator = CommandSeparator

interface ComboboxSelectableItemProps extends React.ComponentProps<
  typeof ComboboxItem
> {
  isSelected: boolean
}

const ComboboxSelectableItem: React.FC<ComboboxSelectableItemProps> = ({
  children,
  className,
  isSelected,
  ...props
}) => (
  <ComboboxItem {...props}>
    <div
      className={twMerge(
        'flex items-center justify-between w-full gap-x-2 text-[13px] text-primary-700 dark:text-primary-300',
        className,
      )}
    >
      {children}
      {isSelected && <HiMiniCheck className="flex-shrink-0 h-4 w-4" />}
    </div>
  </ComboboxItem>
)

Combobox.displayName = 'Combobox'
ComboboxTrigger.displayName = 'ComboboxTrigger'
ComboboxContent.displayName = 'ComboboxContent'
ComboboxInput.displayName = 'ComboboxInput'
ComboboxList.displayName = 'ComboboxList'
ComboboxEmpty.displayName = 'ComboboxEmpty'
ComboboxGroup.displayName = 'ComboboxGroup'
ComboboxItem.displayName = 'ComboboxItem'
ComboboxSeparator.displayName = 'ComboboxSeparator'
ComboboxSelectableItem.displayName = 'ComboboxSelectableItem'

export {
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSeparator,
  ComboboxSelectableItem,
}
